import { httpsCallable, type HttpsCallable } from 'firebase/functions';
import { z } from 'zod';

import { functions } from '../firebase.js';
import { IssuedVoucherSchema, SchemaValidationFailedError, TierSchema } from '@kilat/shared';

/** Typed wrappers around loyalty-related Cloud Functions. */

// ── issueVoucher ─────────────────────────────────────────────────────────

export const IssueVoucherInputSchema = z.object({
  tenantId: z.string().min(1),
  templateId: z.string().min(1),
  customerUid: z.string().min(1),
  expiresAtMs: z.number().int().positive().optional(),
});
export type IssueVoucherInput = z.infer<typeof IssueVoucherInputSchema>;

export const IssueVoucherOutputSchema = z.object({
  voucherId: z.string(),
  ownerUid: z.string(),
});
export type IssueVoucherOutput = z.infer<typeof IssueVoucherOutputSchema>;

const issueVoucherFn: HttpsCallable<IssueVoucherInput, IssueVoucherOutput> = httpsCallable(
  functions,
  'issueVoucher',
);

export async function callIssueVoucher(input: IssueVoucherInput): Promise<IssueVoucherOutput> {
  const parsed = IssueVoucherInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('issueVoucher.input', parsed.error.flatten());
  }
  const result = await issueVoucherFn(parsed.data);
  const out = IssueVoucherOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('issueVoucher.output', out.error.flatten());
  }
  return out.data;
}

// ── bulkIssueVouchers ──────────────────────────────────────────────────────

export const BulkIssueVouchersInputSchema = z.object({
  tenantId: z.string().min(1),
  templateId: z.string().min(1),
  audience: z
    .object({
      kind: z.enum(['all', 'tag', 'tier']),
      tagId: z.string().min(1).optional(),
      tierMin: z.string().min(1).optional(),
    })
    .superRefine((val, ctx) => {
      if (val.kind === 'tag' && !val.tagId) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'tag audience requires tagId' });
      }
      if (val.kind === 'tier' && !val.tierMin) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'tier audience requires tierMin' });
      }
    }),
});
export type BulkIssueVouchersInput = z.infer<typeof BulkIssueVouchersInputSchema>;

export const BulkIssueVouchersOutputSchema = z.object({
  issued: z.number(),
  skipped: z.array(z.object({ customerUid: z.string(), reason: z.string() })),
  failed: z.array(z.object({ customerUid: z.string(), error: z.string() })),
  audienceRequested: z.number(),
  audienceProcessed: z.number(),
  truncated: z.boolean(),
});
export type BulkIssueVouchersOutput = z.infer<typeof BulkIssueVouchersOutputSchema>;

const bulkIssueVouchersFn: HttpsCallable<BulkIssueVouchersInput, BulkIssueVouchersOutput> =
  httpsCallable(functions, 'bulkIssueVouchers');

export async function callBulkIssueVouchers(
  input: BulkIssueVouchersInput,
): Promise<BulkIssueVouchersOutput> {
  const parsed = BulkIssueVouchersInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('bulkIssueVouchers.input', parsed.error.flatten());
  }
  const result = await bulkIssueVouchersFn(parsed.data);
  const out = BulkIssueVouchersOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('bulkIssueVouchers.output', out.error.flatten());
  }
  return out.data;
}

// ── setCustomerTags ──────────────────────────────────────────────────────

export const SetCustomerTagsInputSchema = z.object({
  tenantId: z.string().min(1),
  customerUid: z.string().min(1),
  tagIds: z.array(z.string().min(1)).max(20),
});
export type SetCustomerTagsInput = z.infer<typeof SetCustomerTagsInputSchema>;

export const SetCustomerTagsOutputSchema = z.object({
  customerUid: z.string(),
  tagIds: z.array(z.string()),
  droppedTagIds: z.array(z.string()),
});
export type SetCustomerTagsOutput = z.infer<typeof SetCustomerTagsOutputSchema>;

const setCustomerTagsFn: HttpsCallable<SetCustomerTagsInput, SetCustomerTagsOutput> = httpsCallable(
  functions,
  'setCustomerTags',
);

export async function callSetCustomerTags(
  input: SetCustomerTagsInput,
): Promise<SetCustomerTagsOutput> {
  const parsed = SetCustomerTagsInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('setCustomerTags.input', parsed.error.flatten());
  }
  const result = await setCustomerTagsFn(parsed.data);
  const out = SetCustomerTagsOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('setCustomerTags.output', out.error.flatten());
  }
  return out.data;
}

// ── getTicketCustomerWallet ──────────────────────────────────────────────

export const GetTicketCustomerWalletInputSchema = z.object({
  tenantId: z.string().min(1),
  outletId: z.string().min(1),
  ticketId: z.string().min(1),
});
export type GetTicketCustomerWalletInput = z.infer<typeof GetTicketCustomerWalletInputSchema>;

export const GetTicketCustomerWalletOutputSchema = z.object({
  customerUid: z.string().nullable(),
  tier: TierSchema.nullable(),
  vouchers: z.array(IssuedVoucherSchema),
  /** Store credit the customer holds with this operator, in sen (SPEC D10). */
  creditSen: z.number().int().nonnegative().default(0),
});
export type GetTicketCustomerWalletOutput = z.infer<typeof GetTicketCustomerWalletOutputSchema>;

const getWalletFn: HttpsCallable<GetTicketCustomerWalletInput, GetTicketCustomerWalletOutput> =
  httpsCallable(functions, 'getTicketCustomerWallet');

export async function callGetTicketCustomerWallet(
  input: GetTicketCustomerWalletInput,
): Promise<GetTicketCustomerWalletOutput> {
  const parsed = GetTicketCustomerWalletInputSchema.safeParse(input);
  if (!parsed.success) {
    throw new SchemaValidationFailedError('getTicketCustomerWallet.input', parsed.error.flatten());
  }
  const result = await getWalletFn(parsed.data);
  const out = GetTicketCustomerWalletOutputSchema.safeParse(result.data);
  if (!out.success) {
    throw new SchemaValidationFailedError('getTicketCustomerWallet.output', out.error.flatten());
  }
  return out.data;
}
